import React, {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {BenchlingState} from 'redux-stores/reduceApp';
import {getStore} from 'redux-stores/Store';
import useOnMount from 'ui-library/hooks/useOnMount';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {
  BaseFlowchartConfigStateManager,
  FlowchartConfigStateManagerChildrenProps,
  FlowchartConfigWithPartialMetadata,
  LiveStateChanges,
} from '../useFlowchartState/BaseFlowchartConfigStateManager';
import {
  flowchartConfigAutosaverBlockedChanged,
  flowchartConfigAutosaverPausedChanged,
  flowchartConfigDirtyStateChanged,
  flowchartConfigIsSaveInFlightStateChanged,
  flowchartConfigReadonlyStateChanged,
  flowchartConfigUpdated,
  FlowchartConfigAction,
} from './flowchartConfigActions';
import {FlowchartConfigWithMetadata} from './flowchartConfigAutosaveManagerCallbacks';
import {
  FlowchartConfigAutosaverPausedConditionType,
  FlowchartConfigMetadata,
  hasAnyAutosaverPausedConditions,
} from './reduceFlowchartConfig';

export interface FlowchartConfigReduxStateManagerWrapperProps {
  flowchartConfigId: string;
  persistedFlowchartConfig: StaticTemplateFlowchart;
  flowchartConfigMetadata: FlowchartConfigMetadata;
  children: (props: FlowchartConfigStateManagerChildrenProps) => JSX.Element;
}

function emitAction(action: FlowchartConfigAction): void {
  // Deferring the dispatch ensures we don't trigger a `setState` inside a render cycle
  setTimeout(() => {
    getStore().dispatch(action);
  }, 0);
}

function sendFlowchartConfigDataToRedux(
  flowchartConfigId: string,
  flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata
): void {
  emitAction(flowchartConfigUpdated({flowchartConfigId, ...flowchartConfigWithMetadata}));
}

function setIsFlowchartConfigDirty(flowchartConfigId: string, isDirty: boolean): void {
  emitAction(flowchartConfigDirtyStateChanged(flowchartConfigId, isDirty));
}

function setIsFlowchartConfigReadonly(flowchartConfigId: string, isReadonly: boolean): void {
  emitAction(flowchartConfigReadonlyStateChanged(flowchartConfigId, isReadonly));
}

function setIsFlowchartConfigSaveInFlight(flowchartConfigId: string, isSaveInFlight: boolean): void {
  emitAction(flowchartConfigIsSaveInFlightStateChanged(flowchartConfigId, isSaveInFlight));
}

function setIsFlowchartConfigAutosaverBlocked(flowchartConfigId: string, isBlocked: boolean): void {
  emitAction(flowchartConfigAutosaverBlockedChanged(flowchartConfigId, isBlocked));
}

function setIsFlowchartConfigAutosaverPaused(
  flowchartConfigId: string,
  condition: FlowchartConfigAutosaverPausedConditionType,
  conditionValue: boolean
): void {
  emitAction(flowchartConfigAutosaverPausedChanged(flowchartConfigId, condition, conditionValue));
}

/**
 * FlowchartConfigReduxStateManagerWrapper: Wraps FlowchartConfigStateManager and provides
 * all Redux-specific operations. This component handles:
 * - Reading from Redux store
 * - Dispatching Redux actions
 * - Creating live state change callbacks
 * - Initializing Redux with persisted data
 *
 * The base FlowchartConfigStateManager receives all data through props and doesn't
 * directly interact with Redux.
 */
export function FlowchartConfigReduxStateManagerWrapper(
  props: FlowchartConfigReduxStateManagerWrapperProps
): JSX.Element {
  // Ensure we push the initial version of the flowchart config into Redux. Note that this is async, which is why
  // we have the ?? fallback value immediately below
  useOnMount(() => {
    // If Redux has preserved dirty data from a previous session (e.g., unmounted with
    // block-persistence validation errors), don't overwrite it with server data.
    const existingData = getStore().getState().models.flowchartConfigs[props.flowchartConfigId];
    if (existingData?.componentLiveProperties.isDirty) {
      return;
    }
    sendFlowchartConfigDataToRedux(props.flowchartConfigId, {
      staticFlowchart: props.persistedFlowchartConfig,
      flowchartConfigMetadata: props.flowchartConfigMetadata,
    });
  });

  const flowchartConfigData = useSelector(
    (state: BenchlingState) => state.models.flowchartConfigs[props.flowchartConfigId]
  );

  const draftFlowchartConfigAndMetadata: FlowchartConfigWithMetadata = useMemo(
    () => ({
      staticFlowchart: flowchartConfigData?.staticFlowchart ?? props.persistedFlowchartConfig,
      metadata: flowchartConfigData?.flowchartConfigMetadata ?? props.flowchartConfigMetadata,
    }),
    [
      flowchartConfigData?.staticFlowchart,
      flowchartConfigData?.flowchartConfigMetadata,
      props.persistedFlowchartConfig,
      props.flowchartConfigMetadata,
    ]
  );

  const setIsDirty = useCallback(
    (isDirty: boolean) => {
      setIsFlowchartConfigDirty(props.flowchartConfigId, isDirty);
    },
    [props.flowchartConfigId]
  );

  const setIsReadonly = useCallback(
    (isReadonly: boolean) => {
      setIsFlowchartConfigReadonly(props.flowchartConfigId, isReadonly);
    },
    [props.flowchartConfigId]
  );

  const setIsSaveInFlight = useCallback(
    (isSaveInFlight: boolean) => {
      setIsFlowchartConfigSaveInFlight(props.flowchartConfigId, isSaveInFlight);
    },
    [props.flowchartConfigId]
  );

  const setAutosaverBlocked = useCallback(
    (isBlocked: boolean) => {
      setIsFlowchartConfigAutosaverBlocked(props.flowchartConfigId, isBlocked);
    },
    [props.flowchartConfigId]
  );

  const setAutosaverPaused = useCallback(
    (condition: FlowchartConfigAutosaverPausedConditionType, conditionValue: boolean) => {
      setIsFlowchartConfigAutosaverPaused(props.flowchartConfigId, condition, conditionValue);
    },
    [props.flowchartConfigId]
  );

  const liveStateChanges = useMemo(
    (): LiveStateChanges => ({
      dirtyState: [Boolean(flowchartConfigData?.componentLiveProperties.isDirty), setIsDirty],
      readonlyState: [Boolean(flowchartConfigData?.componentLiveProperties.isReadOnly), setIsReadonly],
      isSaveInFlightState: [
        Boolean(flowchartConfigData?.platformLiveProperties.isSaveInFlight),
        setIsSaveInFlight,
      ],
      isAutosaverPausedState: [
        Boolean(
          hasAnyAutosaverPausedConditions(
            flowchartConfigData?.componentLiveProperties.autosaverPausedConditions ?? new Set()
          )
        ),
        setAutosaverPaused,
      ],
      isAutosaverBlockedState: [
        Boolean(flowchartConfigData?.platformLiveProperties.isAutosaverBlocked),
        setAutosaverBlocked,
      ],
    }),
    [
      flowchartConfigData?.componentLiveProperties.isDirty,
      flowchartConfigData?.componentLiveProperties.isReadOnly,
      flowchartConfigData?.platformLiveProperties.isSaveInFlight,
      flowchartConfigData?.componentLiveProperties.autosaverPausedConditions,
      flowchartConfigData?.platformLiveProperties.isAutosaverBlocked,
      setIsDirty,
      setIsReadonly,
      setIsSaveInFlight,
      setAutosaverPaused,
      setAutosaverBlocked,
    ]
  );

  const setFlowchartConfigData = useCallback(
    (flowchartConfigWithMetadata: FlowchartConfigWithPartialMetadata) => {
      sendFlowchartConfigDataToRedux(props.flowchartConfigId, flowchartConfigWithMetadata);
    },
    [props.flowchartConfigId]
  );

  const lastSaveError = flowchartConfigData?.platformLiveProperties?.lastSaveError ?? null;

  return (
    <BaseFlowchartConfigStateManager
      flowchartConfigData={draftFlowchartConfigAndMetadata}
      setFlowchartConfigData={setFlowchartConfigData}
      liveStateChanges={liveStateChanges}
      lastSaveError={lastSaveError}
    >
      {(baseProps) =>
        props.children({
          ...baseProps,
          persistedFlowchartConfig: props.persistedFlowchartConfig,
          flowchartConfigMetadata: props.flowchartConfigMetadata,
        })
      }
    </BaseFlowchartConfigStateManager>
  );
}
